import {
  createStyleMixin,
  draggableMixin,
  componentNameValidationMixin,
} from '@descope-ui/common/components-mixins';
import { createBaseClass } from '@descope-ui/common/base-classes';
import { compose } from '@descope-ui/common/utils';
import {
  forwardAttrs,
  getComponentName,
  injectStyle,
} from '@descope-ui/common/components-helpers';

export const componentName = getComponentName('timer-button');

const buttonAttrs = [
  'button-variant',
  'button-mode',
  'size',
  'text-align',
  'full-width',
];

const mapButtonAttrs = {
  'button-variant': 'variant',
  'button-mode': 'mode',
};

const timerAttrs = [
  'timer-seconds',
  'timer-hide-icon',
  'timer-paused',
  'size',
  'text-align',
  'full-width',
];

const mapTimerAttrs = {
  'timer-seconds': 'seconds',
  'timer-hide-icon': 'hide-icon',
  'timer-paused': 'paused',
};

const BaseClass = createBaseClass({
  componentName,
  baseSelector: ':host > div',
});

class RawTimerButton extends BaseClass {
  constructor() {
    super();

    this.attachShadow({ mode: 'open' }).innerHTML = `
      <div class="wrapper">
        <descope-timer class="timer"></descope-timer>
        <descope-button class="button" disabled="true">
          <slot></slot>
        </descope-button>
      </div>
		`;

    injectStyle(
      `
      :host {
        display: inline-flex;
      }
      .wrapper {
        display: flex;
        flex-direction: column;
        gap: 0.5em;
        align-items: center;
        width: 100%;
      }
      .timer {
        flex: 1;
      }
    `,
      this,
    );

    this.timer = this.shadowRoot.querySelector('.timer');
    this.button = this.shadowRoot.querySelector('.button');

    this.timer.addEventListener('timer-started', () => this.onTimerStarted());
    this.timer.addEventListener('timer-ended', () => this.onTimerEnded());

    this.button.addEventListener('click', this.onClick.bind(this));
  }

  set onclick(val) {
    this.button.onclick = val;
  }

  init() {
    super.init?.();

    forwardAttrs(this, this.button, {
      includeAttrs: buttonAttrs,
      mapAttrs: mapButtonAttrs,
    });

    forwardAttrs(this, this.timer, {
      includeAttrs: timerAttrs,
      mapAttrs: mapTimerAttrs,
    });

    // When we sync attributes, the `disabled` attribute is being compared to the attribute
    // on Vaadin component; since the Vaadin component has no `disabled` attribute, we sync
    // that back to our component.
    // This is pending a more generic fix in the way we sync attibutes.
    this.button.shadowRoot
      .querySelector('vaadin-button')
      .setAttribute('disabled', 'true');
  }

  onTimerStarted() {
    this.toggleButtonDisable(true);
  }

  onTimerEnded() {
    this.toggleButtonDisable(false);
  }

  onClick() {
    this.timer.reset();
  }

  toggleButtonDisable(isDisabled) {
    setTimeout(() => {
      isDisabled
        ? this.button.setAttribute('disabled', 'true')
        : this.button.removeAttribute('disabled');
    });
  }
}

const { host } = {
  host: { selector: () => ':host' },
  icon: { selector: '.icon' },
  timer: { selector: '.timer' },
};

export const TimerButtonClass = compose(
  createStyleMixin({
    mappings: {
      gap: {},
      flexDirection: {},
      hostWidth: { ...host, property: 'width' },
      hostDirection: { ...host, property: 'direction' },
    },
  }),
  draggableMixin,
  componentNameValidationMixin,
)(RawTimerButton);
